import { ClockIcon } from '@heroicons/react/20/solid';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';

type PaymentPendingBannerProps = {
  group: GroupSubscriptionData;
};

/**
 * Shown while a seat addition is awaiting its async Stripe payment
 * (`pending_seat_addition` non-null). The purchase fires an off-session invoice;
 * capacity + invites only materialize once the `invoice.payment_succeeded`
 * webhook lands, so we surface a "payment pending" state and the caller blocks
 * further seat-count operations until it clears. On `payment_failed` the backend
 * reverts and notifies the purchaser out-of-band — there's no UI failure state.
 */
export function PaymentPendingBanner({ group }: PaymentPendingBannerProps) {
  // Loose null check: during the deploy window an older Swarm omits this field,
  // so it can be `undefined` at runtime despite the type.
  const pending = group.pending_seat_addition;
  if (pending == null || pending <= 0) return null;

  const seatLabel = pending === 1 ? 'seat' : 'seats';

  return (
    <div className="flex gap-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
      <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <p className="font-medium">
          Adding {pending} {seatLabel} — payment pending
        </p>
        <p>The new seats and any queued invites will appear once your payment is confirmed.</p>
      </div>
    </div>
  );
}
